import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Button,
  Badge,
  DropdownMenuItem,
  cn,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Textarea,
  V2Dialog,
  VisuallyHidden,
} from "web-design-system";
import { Link, useNavigate } from "@tanstack/react-router";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import { FundBasket } from "creso-core/types";
import {
  FUND_BASKET_TENURE_OPTIONS,
  FUND_BASKET_STRATEGY_OPTIONS,
} from "creso-core/constants";
import { EllipsisVerticalIcon, PencilIcon, XIcon } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { handleError } from "@/utils/error";
import { useFundBasket, useUpdateFundBasket } from "creso-core/hooks";
import { AmcLogoGroup } from "web-core";
import { BasketIcon } from "./icon";
import { DeleteFundBasket } from "./delete-fund-basket";
import { FundBasketDialog } from "./dialog";
import { BasketActionAccordion } from "./basket-action-accordion";

export function FundBasketSidebar(props: { basket: FundBasket }) {
  const { basket } = props;

  const navigate = useNavigate();

  const [defaultStep, setDefaultStep] = useState(0);

  const description = useMemo(() => {
    const tenure = FUND_BASKET_TENURE_OPTIONS.find(
      (f) => f.value === basket.tenure
    )?.label;
    const strategy = FUND_BASKET_STRATEGY_OPTIONS.find(
      (f) => f.value === basket.strategy
    )?.label;
    return (
      <>
        {tenure} &bull; {strategy}
      </>
    );
  }, [basket]);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem className="text-xs">
            <Link className="text-muted-foreground" to={"/funds/basket"}>
              Baskets
            </Link>
          </BreadcrumbItem>
          {basket.name && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem className="text-primary text-xs">
                {basket.name}
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <BasketIcon strategy={basket.strategy} />
          <ConditionalRenderer condition={basket.basket_type === "custom"}>
            <div className="pointer-events-auto shrink-0">
              <DropdownMenu>
                <div className="group">
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <EllipsisVerticalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="right"
                    align="start"
                    className="w-40"
                  >
                    <DropdownMenuItem
                      onSelect={() => {
                        setDefaultStep(0);
                        setIsEditDialogOpen(true);
                      }}
                    >
                      <PencilIcon className="size-4" />
                      Edit Basket
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="text-destructive">
                      <DeleteFundBasket
                        id={basket.id}
                        onDelete={() =>
                          navigate({
                            to: "/funds/basket",
                          })
                        }
                      />
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </div>
              </DropdownMenu>
            </div>
          </ConditionalRenderer>
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-lg font-semibold wrap-break-word">
            {basket.name}
          </div>
          <span className="text-sm text-muted-foreground">{description}</span>
        </div>
        {basket.basket_type === "curated" ? (
          <Badge
            variant="custom"
            className="text-tag-yellow-foreground bg-tag-yellow w-fit"
          >
            Curated by Creso
          </Badge>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xs text-muted-foreground">Funds</span>
        <div className="flex gap-2 items-center">
          <AmcLogoGroup size="md" items={props.basket.funds_summary} />
          <ConditionalRenderer condition={basket.basket_type === "custom"}>
            <Button
              onClick={() => {
                setDefaultStep(1);
                setIsEditDialogOpen(true);
              }}
              variant="outline"
              className="ml-auto"
              size="icon"
            >
              <PencilIcon />
            </Button>
          </ConditionalRenderer>
        </div>
      </div>
      <div className="mb-auto">
        <BasketRemarkDialog basket={basket} />
      </div>
      <div className="pb-15 flex flex-col gap-4">
        <BasketActionAccordion basketId={basket.id} />
      </div>
      <FundBasketDialog
        defaultStep={defaultStep}
        basketId={basket.id}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
      />
    </>
  );
}

function BasketRemarkDialog({ basket: basketData }: { basket: FundBasket }) {
  const remarkRef = useRef<HTMLTextAreaElement>(null);
  const [open, setOpen] = useState(false);
  const { data: basket } = useFundBasket(basketData.id, {
    enabled: !!basketData.id,
    initialData: basketData,
  });
  const { mutateAsync: updateFundBasket, isPending } = useUpdateFundBasket();

  const handleSave = useCallback(async () => {
    if (!remarkRef.current) return;
    if (!basket) return;
    try {
      await updateFundBasket({
        id: basket.id,
        name: basket.name,
        tenure: basket.tenure,
        strategy: basket.strategy,
        description: remarkRef.current.value,
        funds: basket.funds.map((f) => ({
          fund_scheme: f.fund_scheme_id,
          allocation_percentage: Number(f.allocation_percentage),
        })),
      });
      setOpen(false);
    } catch (error) {
      handleError(error, "Failed to update remark");
    }
  }, [updateFundBasket, basket]);

  if (!basket) return null;

  if (basket.basket_type === "curated" && basket.description) {
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Remark</span>
        <span className="line-clamp-8 text-sm">{basket.description}</span>
      </div>
    );
  }

  return (
    <V2Dialog open={open} onOpenChange={setOpen}>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Remark</span>
        <DialogTrigger
          className={cn(
            "outline-none text-left text-sm group relative pr-4",
            !basket.description && "hover:underline"
          )}
        >
          {basket.description ? (
            <>
              <span className="line-clamp-8 text-sm">{basket.description}</span>
              <div className="hidden group-hover:block p-1 rounded-sm absolute right-0 top-0">
                <PencilIcon className="size-3" />
              </div>
            </>
          ) : (
            <span>Add remark</span>
          )}
        </DialogTrigger>
      </div>
      <DialogContent className="p-4">
        <DialogHeader>
          <VisuallyHidden>
            <DialogTitle>Edit remark</DialogTitle>
            <DialogDescription>Edit remark for {basket.name}</DialogDescription>
          </VisuallyHidden>
          <div className="flex justify-between flex-row items-center">
            <h5>Edit remark for {basket.name}</h5>
            <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
              <XIcon />
            </Button>
          </div>
        </DialogHeader>
        <Textarea
          ref={remarkRef}
          defaultValue={basket.description}
          className="resize-none"
          rows={5}
        />
        <DialogFooter>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (remarkRef.current) {
                remarkRef.current.value = "";
              }
            }}
          >
            Clear
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </V2Dialog>
  );
}
